import { useEffect, useState } from 'react';

import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
  GroupClassIllustrations,
  Tab,
  Text,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { ApiCommand } from '~/lib/Api';
import {
  CalendarListTypeOption,
  REGISTER_FOR_GROUP_OPTION,
  SCHEDULE_APPOINTMENT_OPTION,
} from '~/lib/constants/appointment.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import { getScheduleViewDateHeaderV2 } from '~/lib/util/schedule/schedule.util';

import PatientRegisterConfirmationModal from './modals/PatientRegistrationConfirmationModal';
import RegisterUserForGroupModal from './modals/RegisterUserForGroupModal';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { PatientCalendarEvents } from '~/analytics/patientsCalendarEvents';
import { SelectedGroupState } from '~/lib/types/classes/SelectedGroupState';
import ScheduleFiltersPopover from '~/components/maud/calendar/schedule/ScheduleFiltersPopover';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TOP_LEVEL_ID = 'appointment-header-v2';

export enum CalendarListTypeTitle {
  DAILY_VIEW = 'Daily view',
  GROUPS = 'Groups',
  WEEKLY_VIEW = 'Weekly view',
}

export interface CalendarScheduleTabProps {
  count?: number;
  listType: AppointmentListType;
  title: string;
}

interface NewAppointmentHeaderProps {
  activeTab?: AppointmentListType;
  currentDate: DateTime;
  handleScheduleAppointmentClick: () => void;
  onDateChange: (days: number) => void;
  onTabChange: (tabIndex: AppointmentListType) => void;
  shouldEnableBooking: boolean;
  tabs: CalendarScheduleTabProps[];
  patient: Patient;
  onNewRegistration: () => void;
  showCancelledAppointments: boolean;
  setShowCancelledAppointments: (show: boolean) => void;
}

const NewAppointmentHeader = ({
  activeTab,
  currentDate,
  handleScheduleAppointmentClick,
  onDateChange,
  onTabChange,
  shouldEnableBooking,
  tabs,
  onNewRegistration,
  patient,
  showCancelledAppointments,
  setShowCancelledAppointments,
}: NewAppointmentHeaderProps) => {
  const [selectedTab, setSelectedTab] = useState<AppointmentListType>(
    activeTab ?? CalendarListTypeOption.DAILY_VIEW,
  );

  useEffect(() => {
    if (activeTab && activeTab !== selectedTab) {
      setSelectedTab(activeTab);
    }
  }, [activeTab]);

  const [selectedGroup, setSelectedGroup] =
    useState<SelectedGroupState | null>();
  const [
    isGroupClassRegistrationConfirmationModalOpen,
    setIsGroupClassRegistrationConfirmationModalOpen,
  ] = useState<boolean>(false);

  const selectedGroupClick = ({
    groupClassDetails,
    userIdToRegister,
    userName,
  }: SelectedGroupState) => {
    setSelectedGroup({ groupClassDetails, userIdToRegister, userName });
    setIsGroupClassRegistrationConfirmationModalOpen(true);
  };
  const { trackEvent } = useAnalytics();
  const [isRegisterGroupClassModalOpen, setIsRegisterGroupClassModalOpen] =
    useState<boolean>(false);

  const onCloseRegistrationConfirmationModal = () => {
    setSelectedGroup(null);
    setIsGroupClassRegistrationConfirmationModalOpen(false);
  };

  const handleRegisterUserForGroupModalClose = () => {
    setIsRegisterGroupClassModalOpen(false);
  };
  const handleSuccessfulRegistration = () => {
    onNewRegistration();
    handleRegisterUserForGroupModalClose();
  };

  const { getPatientById: getPatientByIdUrl } = urlConstants.patients;
  const { sendRequest: getPatientById, data: patientData } = useApi<Patient>();

  const {
    viewCancelledAppointmentsInMaud: isViewCancelledAppointmentsInMaudEnabled,
  } = useFlags();

  const handleOnRegisterButtonClick = () => {
    getPatientById({
      command: ApiCommand.GET,
      url: getPatientByIdUrl(patient.userId),
    });
    setIsRegisterGroupClassModalOpen(true);
    trackEvent(PatientCalendarEvents.groupsTabRegisterForGroupButtonClicked, {
      Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
    });
  };

  const handleEventToTrack = (source: string) => {
    let eventToTrack;
    switch (source) {
      case CalendarListTypeTitle.DAILY_VIEW:
        eventToTrack = PatientCalendarEvents.dailyViewTabButtonClicked;
        break;
      case CalendarListTypeTitle.WEEKLY_VIEW:
        eventToTrack = PatientCalendarEvents.weeklyViewTabButtonClicked;
        break;
      case CalendarListTypeTitle.GROUPS:
        eventToTrack = PatientCalendarEvents.groupsTabButtonClicked;
        break;
    }
    trackEvent(eventToTrack, {
      Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
    });
  };

  return (
    <ChakraFlex
      backgroundColor="neutral.background.primary"
      gap="12px"
      id={TOP_LEVEL_ID}
      justify="space-between"
      padding="6px 24px 24px 0px"
      position="relative"
    >
      <ChakraFlex align="center" gap="8px">
        {tabs.map((tab) => (
          <Tab
            counter={tab.count}
            id={`${TOP_LEVEL_ID}__calender-tabs-${tab.listType}`}
            isActive={selectedTab === tab.listType}
            key={tab.title}
            onClick={() => {
              setSelectedTab(tab.listType);
              onTabChange(tab.listType);
              handleEventToTrack(tab.title);
              if (tab.listType === CalendarListTypeOption.GROUPS) {
                trackEvent(PatientCalendarEvents.groupsTabViewed, {
                  Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
                });
              }
            }}
            title={tab.title}
            variant="bubble"
          />
        ))}
      </ChakraFlex>
      {selectedTab === CalendarListTypeOption.GROUPS ? (
        <FireflyButton
          id={`${TOP_LEVEL_ID}__register-group-button`}
          leftIcon={FAIconName.userPlusRegular}
          onClick={handleOnRegisterButtonClick}
          variant="primary"
        >
          {REGISTER_FOR_GROUP_OPTION}
        </FireflyButton>
      ) : (
        <ChakraFlex align="center">
          <ChakraFlex margin="auto" marginRight="18px">
            <Text
              color="primary.90.variant"
              marginRight="18px"
              marginTop="10px"
            >
              {getScheduleViewDateHeaderV2(selectedTab, currentDate)}
            </Text>

            <ChakraFlex>
              <FireflyIconButton
                icon={FAIconName.arrowLeftRegular}
                id={`${TOP_LEVEL_ID}__create-group-class-weekely-leftarrow`}
                onClick={() => onDateChange(-1)}
                variant="tertiary"
              />
              <FireflyIconButton
                icon={FAIconName.arrowRightRegular}
                id={`${TOP_LEVEL_ID}__create-group-class-weekely-rightarrow`}
                onClick={() => {
                  onDateChange(1);
                }}
                variant="tertiary"
              />
            </ChakraFlex>
          </ChakraFlex>
          {shouldEnableBooking && (
            <FireflyButton
              id={`${TOP_LEVEL_ID}__schedule-appointment`}
              leftIcon={FAIconName.calendarPlusRegular}
              onClick={handleScheduleAppointmentClick}
            >
              {SCHEDULE_APPOINTMENT_OPTION}
            </FireflyButton>
          )}
          {isViewCancelledAppointmentsInMaudEnabled && (
            <ScheduleFiltersPopover
              id={`${TOP_LEVEL_ID}__filters`}
              onShowCancelledAppointmentsChange={setShowCancelledAppointments}
              shouldShowCancelledAppointments={showCancelledAppointments}
            />
          )}
        </ChakraFlex>
      )}

      {/* Don't modify line 162 condition. it's effecting functionality */}
      {isRegisterGroupClassModalOpen && patientData && (
        <RegisterUserForGroupModal
          isOpen={isRegisterGroupClassModalOpen}
          onClick={selectedGroupClick}
          onClose={handleRegisterUserForGroupModalClose}
          patient={patientData}
        />
      )}
      <PatientRegisterConfirmationModal
        groupClassExternalId={
          selectedGroup?.groupClassDetails.groupClassExternalId
        }
        groupClassName={selectedGroup?.groupClassDetails?.title}
        Icon={
          selectedGroup?.groupClassDetails.iconName as GroupClassIllustrations
        }
        isOpen={isGroupClassRegistrationConfirmationModalOpen}
        onClose={onCloseRegistrationConfirmationModal}
        onSuccess={handleSuccessfulRegistration}
        userId={selectedGroup?.userIdToRegister}
        userName={selectedGroup?.userName}
      />
    </ChakraFlex>
  );
};
export default NewAppointmentHeader;
